import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import { ConfigService } from '../core/services/config.service';
import {
  TooltipDisplayLookupContext,
  TooltipDisplayRegister,
  TooltipDisplayRegisterContext,
} from './tooltip-display.types';

@Injectable({
  providedIn: 'root',
})
export class TooltipDisplayService {
  private registerSubjects = new Map<string, BehaviorSubject<TooltipDisplayRegister | undefined>>();
  private cache = new Map<string, string | undefined>();
  private readonly staticTooltips: Record<string, string> = {
    'QuoteApplication::ApplicantDetails::applicantname': 'Enter the full legal name of the applicant.',
  };

  constructor(
    private apiService: ApiService,
    private configService: ConfigService
  ) {}

  getRegisterKey(context: TooltipDisplayRegisterContext): string {
    return [context.register || '', context.tab || '', context.section || ''].join('|');
  }

  loadTooltip(context: TooltipDisplayRegisterContext): BehaviorSubject<TooltipDisplayRegister | undefined> {
    const registerKey = this.getRegisterKey(context);
    const existing = this.registerSubjects.get(registerKey);
    if (existing) {
      return existing;
    }

    const subject = new BehaviorSubject<TooltipDisplayRegister | undefined>(undefined);
    this.registerSubjects.set(registerKey, subject);

    this.apiService
      .find<TooltipDisplayRegister>(this.buildUrl(context))
      .subscribe({
        next: (tooltip) => subject.next(tooltip),
        error: () => subject.next(undefined),
      });

    return subject;
  }

  getTooltip(context: TooltipDisplayLookupContext): string | undefined {
    const staticTooltip = this.staticTooltips[this.getTooltipKey(context)];
    if (staticTooltip) {
      return staticTooltip;
    }

    const cacheKey = this.getCacheKey(context);
    const cachedValue = this.cache.get(cacheKey);
    if (cachedValue !== undefined) {
      return cachedValue;
    }

    const register = this.registerSubjects.get(this.getRegisterKey(context))?.getValue();
    const value = register?.items?.find((item) => this.matchItem(item, context))?.value;
    this.cache.set(cacheKey, value);
    return value;
  }

  private buildUrl(context: TooltipDisplayRegisterContext): string {
    const register = encodeURIComponent(context.register);
    let url = `${this.configService.RegisterTooltipUrl}${register}`;
    const queryParts: string[] = [];

    if (context.tab) {
      queryParts.push(`tab=${encodeURIComponent(context.tab)}`);
    }
    if (context.section) {
      queryParts.push(`section=${encodeURIComponent(context.section)}`);
    }

    if (queryParts.length > 0) {
      url += `?${queryParts.join('&')}`;
    }

    return url;
  }

  private matchItem(
    item: { fieldName: string; value: string; tab?: string; section?: string },
    context: TooltipDisplayLookupContext
  ): boolean {
    if (item.fieldName === this.getTooltipKey(context)) {
      return true;
    }

    if (item.fieldName !== context.field) {
      return false;
    }

    const isTabMatch = !context.tab || !item.tab || item.tab === context.tab;
    const isSectionMatch = !context.section || !item.section || item.section === context.section;
    return isTabMatch && isSectionMatch;
  }

  private getTooltipKey(context: TooltipDisplayLookupContext): string {
    return [context.tab || '', context.section || '', context.field || '']
      .filter((part) => part !== '')
      .join('::');
  }

  private getCacheKey(context: TooltipDisplayLookupContext): string {
    return [context.register || '', context.tab || '', context.section || '', context.field || ''].join('|');
  }
}